import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Circle, Group, Stage, Layer, Line, Rect, Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';

import {
  clampZoom,
  downloadFilenameFromUrl,
  fitToStage,
  triggerImageDownload,
  zoomFromCenter,
  type Size,
} from './canvasMath';
import { useStageSize } from './useStageSize';
import {
  renderStrokesToDataUrl,
  screenToImageBrushSize,
  screenToImagePoint,
  type Stroke,
} from './maskMath';
import { renderSketchToDataUrl } from './sketchMath';
import ReefBackground from '../../../components/common/ReefBackground';
import { useStudioStyles } from '../../../hooks/useStudioStyles';
import {
  clampRectToImage,
  isMeaningfulSelection,
  rectFromPoints,
  type ImageRect,
} from './selectionMath';

export type CanvasMode = 'view' | 'mask' | 'sketch' | 'select';

const DEFAULT_SKETCH_SIZE: Size = { width: 1024, height: 1024 };
const DEFAULT_SKETCH_BRUSH_COLOR = 'rgba(20, 20, 20, 0.85)';

export interface KonvaCanvasProps {
  /**
   * Images to display. Empty array renders the empty-state hint (except in
   * sketch mode, where the canvas draws on a blank tablet). Multiple images
   * surface the carousel arrows. The component is controlled when
   * `activeIndex` is provided; otherwise it tracks the active index
   * internally.
   */
  images: string[];
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  emptyHint?: string;
  showControls?: boolean;

  /**
   * Interaction mode.
   * - `'view'` (default): pan/zoom-draggable image.
   * - `'mask'`: brush capture, emits a mask data URL via `onMaskChange`
   *   (opaque white on transparent, sized to the source image).
   * - `'sketch'`: brush capture on a blank tablet, emits a sketch data URL
   *   via `onSketchChange` (dark strokes on opaque white). The tablet's
   *   pixel size is `sketchSize` (default 1024x1024). Images prop is
   *   ignored in this mode.
   * - `'select'`: drag-to-define a rectangular selection on top of the
   *   active image. Emits `onSelectionChange` with image-space coords.
   */
  mode?: CanvasMode;

  /** Brush diameter in screen-space px (mask + sketch). Stored in image space. */
  brushSize?: number;
  /** RGB(A) fill for the on-canvas brush overlay (mask mode). Visual only;
   *  the emitted mask is always opaque white on transparent. */
  brushColor?: string;
  /** Stroke color used in `sketch` mode. Visual + emitted (the rendered
   *  sketch carries this color on a white background). */
  sketchStrokeColor?: string;

  /** Mask emit. Fires after each stroke completes and on `maskClearKey` advance. */
  onMaskChange?: (dataUrl: string | null) => void;
  /** Sketch emit. Same lifecycle as `onMaskChange` but in sketch mode. */
  onSketchChange?: (dataUrl: string | null) => void;
  /** Selection emit. Fires on mouse-up (after a meaningful drag) and on
   *  `selectionClearKey` advance. */
  onSelectionChange?: (rect: ImageRect | null) => void;

  /** Changing this value clears the active mask. */
  maskClearKey?: number | string;
  /** Changing this value clears the active sketch. */
  sketchClearKey?: number | string;
  /** Changing this value clears the active selection. */
  selectionClearKey?: number | string;

  /** Sketch tablet dimensions (mode='sketch' only). */
  sketchSize?: Size;

  /** When set, the canvas surfaces an "Upload" button + drag-drop overlay
   *  in view/mask/select modes. Called with the dropped/picked file. The
   *  parent handles the actual upload + response → image-list lift. */
  onUploadFile?: (file: File) => void;
  /** Mirrors the parent's upload mutation state — disables the button +
   *  flips overlay text to "Uploading…" while in-flight. */
  isUploading?: boolean;
  /**
   * Optional CSS `filter` string composed by the left-rail FilterPanel
   * (e.g. `'brightness(120%) contrast(110%) saturate(80%)'` or `'none'`).
   * Applied to the canvas container so the Konva stage's rendered output
   * is filtered as a single GPU pass — pure preview, doesn't touch the
   * underlying asset.
   */
  previewFilterCss?: string;
  /** Example prompts shown as clickable chips on the empty canvas; clicking
   *  one seeds the Generate panel (via `onExamplePrompt`). */
  examplePrompts?: string[];
  onExamplePrompt?: (prompt: string) => void;
}

const ZOOM_STEP = 1.15;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 8;
const DEFAULT_BRUSH_SIZE = 32;
const DEFAULT_BRUSH_COLOR = 'rgba(232, 65, 24, 0.55)';
const MIN_SELECTION_PX = 4;

export const KonvaCanvas: React.FC<KonvaCanvasProps> = ({
  images,
  activeIndex: controlledIndex,
  onActiveIndexChange,
  emptyHint = 'No image yet. Generate something to see it here.',
  showControls = true,
  mode = 'view',
  brushSize = DEFAULT_BRUSH_SIZE,
  brushColor = DEFAULT_BRUSH_COLOR,
  sketchStrokeColor = DEFAULT_SKETCH_BRUSH_COLOR,
  onMaskChange,
  onSketchChange,
  onSelectionChange,
  maskClearKey,
  sketchClearKey,
  selectionClearKey,
  sketchSize = DEFAULT_SKETCH_SIZE,
  onUploadFile,
  isUploading = false,
  previewFilterCss = 'none',
  examplePrompts,
  onExamplePrompt,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<Konva.Group>(null);
  const stageSize = useStageSize(containerRef);
  const theme = useTheme();
  const studioStyles = useStudioStyles();

  const isSketchMode = mode === 'sketch';
  const isMaskMode = mode === 'mask';
  const isSelectMode = mode === 'select';
  const isBrushMode = isMaskMode || isSketchMode;
  const isInteracting = isBrushMode || isSelectMode;

  // --- carousel state ---
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledIndex !== undefined;
  const activeIndex = Math.min(
    isControlled ? controlledIndex! : internalIndex,
    Math.max(0, images.length - 1),
  );

  const currentUrl = isSketchMode ? undefined : images[activeIndex];

  // --- image loading (skipped in sketch mode — the surface is synthetic) ---
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!currentUrl) {
      setLoadedImage(null);
      return;
    }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = currentUrl;
    img.onload = () => setLoadedImage(img);
    img.onerror = () => setLoadedImage(null);
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [currentUrl]);

  // The "surface size" is the dimensions strokes and selections are stored
  // against. Image size in non-sketch modes, sketch tablet in sketch mode.
  const surfaceSize: Size | null = useMemo(() => {
    if (isSketchMode) return sketchSize;
    if (loadedImage) return { width: loadedImage.width, height: loadedImage.height };
    return null;
  }, [isSketchMode, sketchSize, loadedImage]);

  const recenter = useCallback(() => {
    if (!contentRef.current || !surfaceSize || !stageSize.width || !stageSize.height) return;
    const { scale, position } = fitToStage(surfaceSize, stageSize);
    contentRef.current.scale({ x: scale, y: scale });
    contentRef.current.position(position);
    contentRef.current.getLayer()?.batchDraw();
  }, [surfaceSize, stageSize]);

  useLayoutEffect(() => {
    recenter();
  }, [recenter]);

  const applyZoom = useCallback(
    (factor: number, anchor?: { x: number; y: number }) => {
      if (!contentRef.current) return;
      const node = contentRef.current;
      const newScale = clampZoom(node.scaleX() * factor, MIN_ZOOM, MAX_ZOOM);
      const effectiveFactor = newScale / node.scaleX();
      if (effectiveFactor === 1) return;
      const next = zoomFromCenter(
        node.scaleX(),
        { x: node.x(), y: node.y() },
        effectiveFactor,
        stageSize,
        anchor,
      );
      node.scale({ x: newScale, y: newScale });
      node.position(next.position);
      node.getLayer()?.batchDraw();
    },
    [stageSize],
  );

  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();
      const pointer = e.target.getStage()?.getPointerPosition() ?? undefined;
      const factor = e.evt.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      applyZoom(factor, pointer);
    },
    [applyZoom],
  );

  const changeIndex = useCallback(
    (next: number) => {
      if (images.length === 0) return;
      const clamped = (next + images.length) % images.length;
      if (isControlled) onActiveIndexChange?.(clamped);
      else setInternalIndex(clamped);
    },
    [images.length, isControlled, onActiveIndexChange],
  );

  const handleDownload = useCallback(() => {
    if (!contentRef.current) return;
    const stage = contentRef.current.getStage();
    if (!stage) return;
    const dataUrl = stage.toDataURL({ pixelRatio: 2 });
    triggerImageDownload(dataUrl, downloadFilenameFromUrl(currentUrl, 'image.png'));
  }, [currentUrl]);

  // --- brush state (shared between mask + sketch) ---
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const drawingRef = useRef(false);

  // Clear brush strokes when the underlying surface changes (new image in
  // view/mask, sketchSize change in sketch) or when the parent advances
  // either clearKey.
  useEffect(() => {
    setStrokes([]);
    if (isMaskMode) onMaskChange?.(null);
    if (isSketchMode) onSketchChange?.(null);
     
    // identities can be unstable; we only want to fire on the listed signals.
  }, [currentUrl, maskClearKey, sketchClearKey, sketchSize.width, sketchSize.height]);

  // Emit on stroke completion. Branches by mode so the right rendering
  // pipeline (mask vs sketch) and emit callback fire.
  const emitBrush = useCallback(
    (next: Stroke[]) => {
      if (!surfaceSize) return;
      if (isMaskMode && onMaskChange) {
        onMaskChange(renderStrokesToDataUrl(next, surfaceSize));
      } else if (isSketchMode && onSketchChange) {
        onSketchChange(
          renderSketchToDataUrl(next, surfaceSize, {
            strokeColor: sketchStrokeColor,
            backgroundColor: 'rgba(255, 255, 255, 1)',
          }),
        );
      }
    },
    [isMaskMode, isSketchMode, surfaceSize, onMaskChange, onSketchChange, sketchStrokeColor],
  );

  const getImageSpacePointer = useCallback(
    (stage: Konva.Stage | null): { point: { x: number; y: number }; size: number } | null => {
      if (!stage || !contentRef.current) return null;
      const pointer = stage.getPointerPosition();
      if (!pointer) return null;
      const transform = {
        position: { x: contentRef.current.x(), y: contentRef.current.y() },
        scale: contentRef.current.scaleX(),
      };
      return {
        point: screenToImagePoint(pointer, transform),
        size: screenToImageBrushSize(brushSize, transform),
      };
    },
    [brushSize],
  );

  const handleStrokeStart = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!isBrushMode || !surfaceSize) return;
      const seed = getImageSpacePointer(e.target.getStage());
      if (!seed) return;
      drawingRef.current = true;
      setStrokes(prev => [...prev, { points: [seed.point.x, seed.point.y], brushSize: seed.size }]);
    },
    [isBrushMode, surfaceSize, getImageSpacePointer],
  );

  const handleStrokeMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!drawingRef.current || !isBrushMode) return;
      const tick = getImageSpacePointer(e.target.getStage());
      if (!tick) return;
      setStrokes(prev => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        const updated: Stroke = {
          ...last,
          points: [...last.points, tick.point.x, tick.point.y],
        };
        return [...prev.slice(0, -1), updated];
      });
    },
    [isBrushMode, getImageSpacePointer],
  );

  const handleStrokeEnd = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setStrokes(prev => {
      emitBrush(prev);
      return prev;
    });
  }, [emitBrush]);

  // --- selection state ---
  const [selectionRect, setSelectionRect] = useState<ImageRect | null>(null);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setSelectionRect(null);
    if (isSelectMode) onSelectionChange?.(null);
     
  }, [currentUrl, selectionClearKey]);

  const handleSelectionStart = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!isSelectMode) return;
      const seed = getImageSpacePointer(e.target.getStage());
      if (!seed) return;
      selectionStartRef.current = seed.point;
      setSelectionRect({ x: seed.point.x, y: seed.point.y, width: 0, height: 0 });
    },
    [isSelectMode, getImageSpacePointer],
  );

  const handleSelectionMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!isSelectMode || !selectionStartRef.current) return;
      const tick = getImageSpacePointer(e.target.getStage());
      if (!tick) return;
      setSelectionRect(rectFromPoints(selectionStartRef.current, tick.point));
    },
    [isSelectMode, getImageSpacePointer],
  );

  const handleSelectionEnd = useCallback(() => {
    if (!isSelectMode || !selectionStartRef.current) return;
    selectionStartRef.current = null;
    setSelectionRect(prev => {
      if (!prev || !surfaceSize) return prev;
      if (!isMeaningfulSelection(prev, MIN_SELECTION_PX)) {
        // Treat tiny drags as clicks: clear the selection.
        onSelectionChange?.(null);
        return null;
      }
      const clamped = clampRectToImage(prev, surfaceSize);
      onSelectionChange?.(clamped);
      return clamped;
    });
  }, [isSelectMode, surfaceSize, onSelectionChange]);

  // --- top-level stage handlers fan out to per-mode handlers ---
  const handleDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (isBrushMode) return handleStrokeStart(e);
      if (isSelectMode) return handleSelectionStart(e);
    },
    [isBrushMode, isSelectMode, handleStrokeStart, handleSelectionStart],
  );
  const handleMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (isBrushMode) return handleStrokeMove(e);
      if (isSelectMode) return handleSelectionMove(e);
    },
    [isBrushMode, isSelectMode, handleStrokeMove, handleSelectionMove],
  );
  const handleUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (isBrushMode) return handleStrokeEnd();
      if (isSelectMode) return handleSelectionEnd();
      void e;
    },
    [isBrushMode, isSelectMode, handleStrokeEnd, handleSelectionEnd],
  );

  // --- upload state ---
  // Drag-drop counter approach: dragenter/dragleave bubble through child
  // elements, so a simple boolean would flicker. Counting opens-vs-closes
  // gives a stable overlay state.
  const [dragDepth, setDragDepth] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadEnabled = !!onUploadFile && !isSketchMode;
  const isDraggingFile = uploadEnabled && dragDepth > 0;

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!uploadEnabled) return;
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      setDragDepth(d => d + 1);
    },
    [uploadEnabled],
  );
  const handleDragLeave = useCallback(() => {
    if (!uploadEnabled) return;
    setDragDepth(d => Math.max(0, d - 1));
  }, [uploadEnabled]);
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!uploadEnabled) return;
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [uploadEnabled],
  );
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!uploadEnabled || !onUploadFile) return;
      e.preventDefault();
      setDragDepth(0);
      const file = e.dataTransfer.files?.[0];
      if (file) onUploadFile(file);
    },
    [uploadEnabled, onUploadFile],
  );

  // Empty-canvas guard: only return early when we have nothing to draw AND
  // upload isn't an option. When upload is enabled the empty state should
  // still let the user drag-drop a file in; we keep the regular canvas
  // shell so the drop target stays mounted.
  if (images.length === 0 && !isSketchMode && !uploadEnabled) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          position: 'relative',
          bgcolor: 'background.default',
          borderRadius: 1,
          border: theme => `1px dashed ${theme.palette.divider}`,
        }}
        aria-label="Image canvas (empty)"
      >
        {studioStyles.showReefBg && <ReefBackground />}
        <EmptyOverlay hint={emptyHint} uploadable={false} />
      </Box>
    );
  }

  const cursor = isBrushMode ? 'crosshair' : isSelectMode ? 'crosshair' : 'default';
  const groupDraggable = !isInteracting;

  return (
    <Box
      ref={containerRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        bgcolor: 'background.default',
        borderRadius: 1,
        border: theme =>
          isDraggingFile
            ? `2px dashed ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        // Live preview CSS filter from the left-rail FilterPanel.
        // Composed in `filterState.filtersToCss` — `'none'` when neutral
        // so the browser skips the GPU pass; otherwise stacks brightness/
        // contrast/saturate/hue/grayscale/sepia/invert/blur.
        filter: previewFilterCss,
        cursor,
      }}
    >
      {/* Signature chrome, behind everything the stage draws. Which theme (if
          any) has one is decided in useStudioStyles, so the canvas carries no
          theme conditional of its own. */}
      {studioStyles.showReefBg && <ReefBackground />}
      {images.length === 0 && !isSketchMode && (
        <EmptyOverlay
          hint={emptyHint}
          uploadable={uploadEnabled}
          onPickImage={() => fileInputRef.current?.click()}
          examplePrompts={examplePrompts}
          onExamplePrompt={onExamplePrompt}
        />
      )}
      {stageSize.width > 0 && stageSize.height > 0 && (images.length > 0 || isSketchMode) && (
        <Stage
          width={stageSize.width}
          height={stageSize.height}
          onWheel={handleWheel}
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={handleUp}
          onMouseLeave={handleUp}
          onTouchStart={handleDown}
          onTouchMove={handleMove}
          onTouchEnd={handleUp}
        >
          <Layer>
            <Group ref={contentRef} draggable={groupDraggable}>
              {/* The "surface" — a real image in view/mask/select; a synthetic
                  white rect tablet in sketch mode. */}
              {isSketchMode ? (
                <Rect
                  x={0}
                  y={0}
                  width={sketchSize.width}
                  height={sketchSize.height}
                  fill="white"
                  // The tablet is white on a `background.default` ground. On a
                  // dark ground its edge is obvious; on a warm light one white
                  // on cream is ~1.2:1 and the sheet dissolves. A hairline
                  // keeps the drawable area findable in either mode.
                  stroke={theme.palette.divider}
                  strokeWidth={1}
                  listening={false}
                />
              ) : (
                loadedImage && <KonvaImage image={loadedImage} />
              )}

              {/* Brush strokes (mask + sketch). Color comes from props so
                  sketch can show a dark stroke while mask shows red. */}
              {strokes.map((stroke, i) => {
                const strokeColor = isSketchMode ? sketchStrokeColor : brushColor;
                return stroke.points.length === 2 ? (
                  <Circle
                    key={`brush-dot-${i}`}
                    x={stroke.points[0]}
                    y={stroke.points[1]}
                    radius={stroke.brushSize / 2}
                    fill={strokeColor}
                    listening={false}
                  />
                ) : (
                  <Line
                    key={`brush-stroke-${i}`}
                    points={stroke.points}
                    stroke={strokeColor}
                    strokeWidth={stroke.brushSize}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                  />
                );
              })}

              {/* Selection rectangle overlay. */}
              {isSelectMode && selectionRect && selectionRect.width > 0 && selectionRect.height > 0 && (
                <Rect
                  x={selectionRect.x}
                  y={selectionRect.y}
                  width={selectionRect.width}
                  height={selectionRect.height}
                  fill="rgba(64, 160, 255, 0.15)"
                  stroke="rgba(64, 160, 255, 0.9)"
                  strokeWidth={2}
                  dash={[6, 4]}
                  listening={false}
                />
              )}
            </Group>
          </Layer>
        </Stage>
      )}

      {showControls && (images.length > 0 || isSketchMode) && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(20,22,28,0.72)'
              : 'rgba(255,255,255,0.85)',
            border: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
            backdropFilter: 'blur(8px)',
          }}
        >
          {images.length > 1 && !isSketchMode && (
            <>
              <Tooltip title="Previous">
                <IconButton size="small" onClick={() => changeIndex(activeIndex - 1)}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ minWidth: 48, textAlign: 'center' }}>
                {activeIndex + 1} / {images.length}
              </Typography>
              <Tooltip title="Next">
                <IconButton size="small" onClick={() => changeIndex(activeIndex + 1)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Zoom out">
            <IconButton size="small" onClick={() => applyZoom(1 / ZOOM_STEP)}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fit">
            <IconButton size="small" onClick={recenter}>
              <CenterFocusStrongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom in">
            <IconButton size="small" onClick={() => applyZoom(ZOOM_STEP)}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!isSketchMode && images.length > 0 && (
            <Tooltip title="Download">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {uploadEnabled && (
            <Tooltip title={isUploading ? 'Uploading…' : 'Upload image'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <UploadFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Hidden file input — opened by the Upload button click. Reset
          target.value after pick so re-picking the same file fires
          onChange again. */}
      {uploadEnabled && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={e => {
            const file = e.target.files?.[0];
            if (file && onUploadFile) onUploadFile(file);
            e.target.value = '';
          }}
        />
      )}

      {/* Drag-drop visual overlay. Pointer-events:none so it doesn't eat
          drop events the container is listening for. */}
      {isDraggingFile && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(64, 160, 255, 0.12)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Stack alignItems="center" spacing={1}>
            <UploadFileIcon sx={{ fontSize: 56, opacity: 0.8 }} />
            <Typography variant="body2" fontWeight={600}>
              Drop to upload
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

/**
 * Centered "no images yet" overlay. Lives inside the canvas container so
 * the drag-drop handlers on the outer Box still get drop events when the
 * user drops a file before any image exists.
 */
const EmptyOverlay: React.FC<{
  hint: string;
  uploadable: boolean;
  /** Opens the file picker — wired to the main upload affordance. */
  onPickImage?: () => void;
  examplePrompts?: string[];
  onExamplePrompt?: (prompt: string) => void;
}> = ({ hint, uploadable, onPickImage, examplePrompts, onExamplePrompt }) => {
  const showExamples = !!examplePrompts?.length && !!onExamplePrompt;
  const clickable = uploadable && !!onPickImage;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        // The overlay itself stays click-through so drag-drop keeps working;
        // only the interactive affordances (dropzone, chips) opt back in.
        pointerEvents: 'none',
        p: 3,
      }}
      aria-label="Image canvas (empty)"
    >
      {/* Center: the primary upload affordance. The icon IS the picker. */}
      <Stack flex={1} alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 0 }}>
        <ButtonBase
          onClick={clickable ? onPickImage : undefined}
          disabled={!clickable}
          focusRipple
          aria-label={clickable ? 'Upload an image' : undefined}
          sx={{
            pointerEvents: clickable ? 'auto' : 'none',
            width: 128,
            height: 128,
            borderRadius: 4,
            border: theme => `2px dashed ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            color: 'text.secondary',
            transition: 'transform 150ms ease, border-color 150ms, color 150ms, background-color 150ms',
            '&:hover': clickable
              ? {
                  transform: 'translateY(-2px)',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: 'action.hover',
                }
              : undefined,
          }}
        >
          <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 52 }} />
          {clickable && (
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Upload
            </Typography>
          )}
        </ButtonBase>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Let&apos;s make something
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 380, mx: 'auto', mt: 0.5 }}
          >
            {clickable
              ? 'Click to upload, drag an image in, or describe what you want to create.'
              : hint}
          </Typography>
        </Box>
      </Stack>

      {/* Footer: modern example-prompt suggestions, pinned to the bottom. */}
      {showExamples && (
        <Stack alignItems="center" spacing={1} sx={{ pointerEvents: 'none' }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
            <AutoAwesomeIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
              Try an example
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
            useFlexGap
            sx={{ pointerEvents: 'auto', rowGap: 1, maxWidth: 760 }}
          >
            {examplePrompts!.map(prompt => (
              <Chip
                key={prompt}
                label={prompt}
                size="small"
                clickable
                onClick={() => onExamplePrompt!(prompt)}
                sx={{
                  maxWidth: 300,
                  height: 'auto',
                  minHeight: 28,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  transition: 'transform 120ms ease, background-color 120ms, border-color 120ms',
                  '&:hover': {
                    bgcolor: 'action.selected',
                    borderColor: 'primary.main',
                    transform: 'translateY(-1px)',
                  },
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    display: 'block',
                    py: 0.5,
                    px: 0.5,
                    lineHeight: 1.3,
                    fontSize: '0.75rem',
                  },
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
